import React, { useRef, useState, useEffect } from 'react';
import Cookies from "js-cookie";
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { X } from 'react-bootstrap-icons';
import MainProfile from "../innerSection/MainProfile";
import "../innerSection/innersection.css"
import useMicroSiteEncrypt from '../CustomHook/useMicroSiteEncrypt';
import { eventTracking, fetchTrackingDetails } from '../../redux/actions/micrositeAction';
import ProductCarousel from '../carouselSection/ProductCarousel';
import StoriesCarousel from '../carouselSection/StoriesCarousel';
import LeadForm from '../innerSection/LeadForm';
import WhatsappPreviewModal from '../innerSection/WhatsappPreviewModal';
import WhatsappButton from '../innerSection/WhatsappButton';
import RecaptchaComponent from '../commonSection/RecaptchaComponent';
import Disclaimer from '../commonSection/Disclaimer';
import { IdleTimerProvider } from 'react-idle-timer';

// Returns YouTube embed URL if url is YouTube, otherwise null
const getYoutubeEmbedUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const u = url.trim();
    const ytMatch = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return ytMatch ? `https://www.youtube.com/embed/${ytMatch[1]}` : null;
};

const MainPageContainer = (props) => {
    const dispatch = useDispatch();
    const idleTimerRef = useRef(null);
    const intervalRef = useRef(null);
    const [timeoutId, setTimeoutId] = useState(null); // State to track timeout ID
    const [hasCalledApi, setHasCalledApi] = useState(false); // State to track if API call has been made
    const home_data = useSelector((state) => state.microsite?.home_data);
    const current_flow = useSelector((state) => state.microsite?.home_data?.flow);
    const theme_colors = useSelector((state) => state.microsite?.theme_colors);
    const solution_data = useSelector((state) => state.microsite?.solution_data);
    const tracking_details = useSelector((state) => state.microsite?.tracking_details);
    const { microSiteEncrypt, token, error } = useMicroSiteEncrypt();
    const [open, setOpen] = useState(false);
    const hasTrdInUrl = () => Boolean(new URLSearchParams(window?.location?.search || '').get('trd'));
    const [showAssetsPopup, setShowAssetsPopup] = useState(hasTrdInUrl);
    const [imageZoom, setImageZoom] = useState(1);
    const previousUrlRef = useRef(window?.location?.href);
    const popupAssets = home_data?.popup_assets || {};
    const hasPopupImage = popupAssets?.image;
    const hasPopupVideo = popupAssets?.video;
    const cFile = tracking_details?.c_file;
    // Prefer explicit type/content_type when it is one of the known values,
    // otherwise fall back to inferring from the file URL.
    const rawType = (tracking_details?.type || tracking_details?.content_type || '').toLowerCase();
    const inferredType = (() => {
        if (!cFile || typeof cFile !== 'string') return '';
        if (/\.pdf(?:\?|#|$)/i.test(cFile)) return 'pdf';
        if (/\.(jpg|jpeg|png|gif|webp)(?:\?|#|$)/i.test(cFile)) return 'image';
        if (getYoutubeEmbedUrl(cFile)) return 'video';
        if (/\.(mp4|webm|ogg)(?:\?|#|$)/i.test(cFile)) return 'video';
        return '';
    })();
    const displayType = ['pdf', 'image', 'video'].includes(rawType) ? rawType : inferredType;
    const showTrackingAsset = cFile && (displayType === 'pdf' || displayType === 'image' || displayType === 'video');
    const siteColors = {
        primary: theme_colors?.primary,  // Blue color
        secondary: theme_colors?.secondary,  // Orange color
        textMuted: "#00000070"  // Semi-transparent black
    };
    const captcha_key =
    window.location.origin.includes("spdevmfp") ||
    window.location.origin.includes("localhost")
      ? "6LfunNgbAAAAAEM0X3hcwSBYKJwIqHmmInfz0uFK"
      : window.location.origin.includes("sppreprod") ||
        window.location.origin.includes("sptestmfp")
      ? "6Ldeg6YaAAAAAAZqU7AQ_j-lG4LcnwP4jfNFiCJJ"
      : "6Lf9fqoaAAAAAH8JhnTg9gLI3KsYKmR2tdCRVg8i";

    // Prepare meta tag values
    const title = home_data?.tracking_details?.c_title || home_data?.microsite?.name || "Microsite";
    const description = home_data?.tracking_details?.c_description || home_data?.microsite?.description || home_data?.microsite?.bio || "Connect with us for financial advice and services.";
    const image = home_data?.tracking_details?.c_thumb_image || home_data?.microsite?.profile_image || "";
    const currentUrl = typeof window !== 'undefined' ? window.location.href : "";
    // When trd is present in URL, fetch tracking details and open popup
    const trdParam = new URLSearchParams(window?.location?.search || '').get('trd');

    useEffect(() => {
        if (trdParam) {
            dispatch(fetchTrackingDetails(trdParam));
        }
    }, [trdParam]);

    // Set meta tags (title, description, image) from tracking_details when available
    useEffect(() => {
        if (!tracking_details) return;

        const title = tracking_details?.c_title;
        const description = tracking_details?.c_description;
        const typeForMeta = (tracking_details?.type || tracking_details?.content_type || '').toLowerCase();
        
        // Get image URL: prefer c_thumb_image, fallback to c_file if type is image
        let imageUrl = tracking_details?.c_thumb_image;
        if (!imageUrl && typeForMeta === 'image' && tracking_details?.c_file) {
            imageUrl = tracking_details.c_file;
        }

        // Set document title
        if (title) {
            document.title = title;
        }

        // Helper to set or update meta tags
        const setMeta = (attr, key, value) => {
            if (!value || value.trim() === '') return; // Skip if value is empty or whitespace
            const selector = attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                if (attr === 'name') {
                    el.setAttribute('name', key);
                } else {
                    el.setAttribute('property', key);
                }
                document.head.appendChild(el);
            }
            el.setAttribute('content', value);
        };
        
        // Set Open Graph meta tags
        if (title && title.trim()) {
            setMeta('property', 'og:title', title);
        }
        if (description && description.trim()) {
            setMeta('property', 'og:description', description);
        }
        if (imageUrl && imageUrl.trim()) {
            setMeta('property', 'og:image', imageUrl);
        }
        // og:url = current page URL (includes trd token in query when present)
        setMeta('property', 'og:url', window.location.href);
        setMeta('property', 'og:type', 'website');

        // // Set Twitter Card meta tags
        // if (title && title.trim()) {
        //     setMeta('name', 'twitter:title', title);
        // }
        // if (description && description.trim()) {
        //     setMeta('name', 'twitter:description', description);
        // }
        // if (imageUrl && imageUrl.trim()) {
        //     setMeta('name', 'twitter:image', imageUrl);
        // }
    }, [tracking_details]);

      useEffect(() => {
        const startTimeout = setTimeout(() => {
            generateEventAction("start");
        }, 2000);
            
        intervalRef.current = setInterval(() => {
            generateEventAction("call");
        }, 20000);

        const openTimeout = setTimeout(() => {
            setOpen(true);
        }, 8000);

        // Cleanup function to run when component unmounts
        return () => {
            clearTimeout(startTimeout);
            clearTimeout(openTimeout);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            generateEventAction("end");
        }
    }, []); 
    
    const getRecaptchaToken = async () => {
        return new Promise((resolve, reject) => {
        // Load the reCAPTCHA script
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${captcha_key}`;
        script.async = true;
        script.onload = () => {
            window.grecaptcha.ready(() => {
            window.grecaptcha.execute(captcha_key, { action: 'submit' })
                .then(token => {
                resolve(token);
                })
                .catch(err => {
                reject(err);
                });
            });
        };
        document.body.appendChild(script);
        });
    };
    
    const getNewRecaptchaToken = async (captcha_key) => {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?render=${captcha_key}`;
            script.async = true;
            script.onload = () => {
                window.grecaptcha.ready(() => {
                    window.grecaptcha.execute(captcha_key, { action: 'submit' })
                        .then(token => {
                            resolve(token);
                        })
                        .catch(err => {
                            reject(err);
                        });
                });
            };
            document.body.appendChild(script);
        });
    };
    
    const generateEventAction = async (action) => {
        let tokenEvent = "";
        let url = window?.location?.href;
        
        // Get a fresh recaptcha token for event tracking
        let recaptcha = await getNewRecaptchaToken(captcha_key);
        
        // For "end" action, use the previous URL
        if (action === "end") {
            url = previousUrlRef.current;
        } else {
            // Update the previous URL ref with current URL for next time
            previousUrlRef.current = url;
        }
        
        // Update URL handling
        if (url.includes('/website/category')) {
            url = url.replace('/website/category', '');
        } else if (url.includes('/website')) {
            url = url.replace('/website', '');
        }
        
        let flow = "homepage";
        let contact_id = "";
        if (Cookies.get("new_c")) {
            contact_id = Cookies.get("new_c");
        } else if (Cookies.get("c")) {
            contact_id = Cookies.get("c");
        }
        
        let end_time = new Date();
        tokenEvent = await microSiteEncrypt();
        if (!Cookies.get("vtkn")) {
            Cookies.set("vtkn", tokenEvent);
        }
        if (tokenEvent && tokenEvent !== "") {
            dispatch(eventTracking(tokenEvent, recaptcha, url, action, (action === "call" || action === "end" || action === "idle") ? formatDateToISD(end_time) : "", flow, contact_id));
        }
    };
    
    function formatDateToISD(dateString) {
        const date = new Date(dateString);

        // Helper function to add leading zero
        const padZero = (num) => (num < 10 ? `0${num}` : num);

        const year = date.getFullYear();
        const month = padZero(date.getMonth() + 1); // Months are zero-indexed
        const day = padZero(date.getDate());
        const hours = padZero(date.getHours());
        const minutes = padZero(date.getMinutes());
        const seconds = padZero(date.getSeconds());

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
    
    const scrollToSection = (id) => {
        const section = document.getElementById(id);
        const headerOffset = 79.98; // Adjust based on the header height
        const elementPosition = section.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerOffset;

        window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
        });
    };

    const handleOnIdle = () => {
        if (!hasCalledApi) {
            console.log("User is idle. Setting up timeout for API call.");

            // Set a timeout to trigger the API call after 20 seconds
            const id = setTimeout(() => {
                console.log("Making API call after 20 seconds of being idle.");
                generateEventAction("idle");
                
                clearInterval(intervalRef.current);
                
                setHasCalledApi(true); // Mark that API call has been made
            }, 10*60*1000);

            setTimeoutId(id);
        }
    };

    const handleOnActive = () => {
        console.log("User is active.");

        // Reload the page if the API call was already made once
        if (hasCalledApi) {
            console.log("Reloading page since API call was already made once.");
            window.location.reload();
        }

        // Clear the timeout if the user becomes active
        if (timeoutId) {
            clearTimeout(timeoutId);
            setTimeoutId(null);
        }
    };

    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={currentUrl} />
                {image && (
                    <>
                        <meta property="og:image" content={image} />
                        <meta property="og:image:width" content="1200" />
                        <meta property="og:image:height" content="630" />
                    </>
                )}
            </Helmet>
            <IdleTimerProvider
                ref={idleTimerRef}
                timeout={1000} // 1 minute timeout
                onIdle={handleOnIdle}
                onActive={handleOnActive}
            >
              
            {/* Assets popup - microsite-themed design with c_file (PDF/image/video) */}
            <Transition show={showAssetsPopup} as={React.Fragment}>
                <Dialog as="div" className="relative z-[100]" onClose={() => setShowAssetsPopup(false)}>
                    <TransitionChild
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/55 backdrop-blur-sm" aria-hidden="true" />
                    </TransitionChild>
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <TransitionChild
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel
                                className="relative w-full max-w-2xl rounded-2xl overflow-hidden flex flex-col shadow-2xl ring-1 ring-black/5"
                                style={{
                                    backgroundColor: '#fff',
                                    borderTop: `4px solid ${siteColors.primary || '#2167A7'}`,
                                }}
                            >
                                {/* Header bar - microsite primary */}
                                <div
                                    className="flex items-center justify-between px-4 py-1 text-white"
                                    style={{ backgroundColor: siteColors.primary || '#2167A7' }}
                                >
                                    <h3 className="text-sm font-medium truncate pr-2">
                                        {showTrackingAsset && tracking_details?.c_title ? tracking_details.c_title : 'Asset'}
                                    </h3>
                                    <button
                                        type="button"
                                        onClick={() => { setShowAssetsPopup(false); setImageZoom(1); }}
                                        className="flex-shrink-0 rounded-full p-1.5 hover:bg-white/20 transition-colors"
                                        aria-label="Close"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>

                                {/* Content area - fixed frame */}
                                <div className="flex flex-col flex-1 min-h-0 p-1">
                                    <div
                                        className="relative w-full h-[68vh] min-h-[300px] max-h-[82vh] overflow-hidden flex items-center justify-center"
                                        style={{ backgroundColor: siteColors.textMuted ? `${siteColors.textMuted}12` : '#f1f5f9' }}
                                    >
                                        {showTrackingAsset ? (
                                            <>
                                                {displayType === 'pdf' && (
                                                    <iframe
                                                        src={`${cFile}#toolbar=1`}
                                                        title={tracking_details?.c_title || 'PDF'}
                                                        className="w-full h-full border-0"
                                                    />
                                                )}
                                                {displayType === 'image' && (
                                                    <>
                                                        <div className="w-full h-full overflow-auto flex items-start justify-center min-h-0 min-w-0">
                                                            <div
                                                                className="flex-shrink-0 rounded-lg overflow-hidden"
                                                                style={{ width: `${100 * imageZoom}%`, minWidth: '100%', height: `${100 * imageZoom}%`, minHeight: '100%' }}
                                                            >
                                                                <img
                                                                    src={cFile}
                                                                    alt={tracking_details?.c_title || 'Asset'}
                                                                    className="w-full h-full object-contain rounded-lg block"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="absolute bottom-3 right-3 flex gap-1 rounded-lg overflow-hidden border shadow-sm" style={{ backgroundColor: siteColors.primary ? `${siteColors.primary}18` : 'rgba(0,0,0,0.06)' }}>
                                                            <button type="button" onClick={() => setImageZoom((z) => Math.max(0.5, z - 0.25))} className="px-2 py-1.5 text-sm font-medium hover:opacity-80" aria-label="Zoom out">-</button>
                                                            <span className="px-2 py-1.5 text-xs tabular-nums">{Math.round(imageZoom * 100)}%</span>
                                                            <button type="button" onClick={() => setImageZoom((z) => Math.min(3, z + 0.25))} className="px-2 py-1.5 text-sm font-medium hover:opacity-80" aria-label="Zoom in">+</button>
                                                        </div>
                                                    </>
                                                )}
                                                {displayType === 'video' && (
                                                    <div className="w-full h-full min-w-0 min-h-0 flex items-center justify-center overflow-hidden">
                                                        {getYoutubeEmbedUrl(cFile) ? (
                                                            <iframe
                                                                src={getYoutubeEmbedUrl(cFile)}
                                                                title={tracking_details?.c_title || 'YouTube video'}
                                                                className="w-full h-full min-w-0 min-h-0 rounded-lg border-0"
                                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                allowFullScreen
                                                            />
                                                        ) : (
                                                            <video
                                                                className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg"
                                                                controls
                                                                autoPlay
                                                                muted
                                                                playsInline
                                                            >
                                                                <source src={cFile} type={cFile.match(/\.webm$/i) ? 'video/webm' : cFile.match(/\.ogg$/i) ? 'video/ogg' : 'video/mp4'} />
                                                                Your browser does not support the video tag.
                                                            </video>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        ) : hasPopupVideo ? (
                                            <div className="w-full h-full min-w-0 min-h-0 flex items-center justify-center overflow-hidden">
                                                {getYoutubeEmbedUrl(popupAssets.video) ? (
                                                    <iframe
                                                        src={getYoutubeEmbedUrl(popupAssets.video)}
                                                        title="Popup video"
                                                        className="w-full h-full min-w-0 min-h-0 rounded-lg border-0"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        allowFullScreen
                                                    />
                                                ) : (
                                                    <video
                                                        className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg"
                                                        controls
                                                        autoPlay
                                                        muted
                                                        loop
                                                        playsInline
                                                    >
                                                        <source src={popupAssets.video} type="video/mp4" />
                                                        Your browser does not support the video tag.
                                                    </video>
                                                )}
                                            </div>
                                        ) : hasPopupImage ? (
                                            <>
                                                <div className="w-full h-full overflow-auto flex items-start justify-center min-h-0 min-w-0">
                                                    <div
                                                        className="flex-shrink-0 rounded-lg overflow-hidden"
                                                        style={{ width: `${100 * imageZoom}%`, minWidth: '100%', height: `${100 * imageZoom}%`, minHeight: '100%' }}
                                                    >
                                                        <img
                                                            src={popupAssets.image}
                                                            alt="Popup asset"
                                                            className="w-full h-full object-contain rounded-lg block"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="absolute bottom-3 right-3 flex gap-1 rounded-lg overflow-hidden border shadow-sm" style={{ backgroundColor: siteColors.primary ? `${siteColors.primary}18` : 'rgba(0,0,0,0.06)' }}>
                                                    <button type="button" onClick={() => setImageZoom((z) => Math.max(0.5, z - 0.25))} className="px-2 py-1.5 text-sm font-medium hover:opacity-80" aria-label="Zoom out">-</button>
                                                    <span className="px-2 py-1.5 text-xs tabular-nums">{Math.round(imageZoom * 100)}%</span>
                                                    <button type="button" onClick={() => setImageZoom((z) => Math.min(3, z + 0.25))} className="px-2 py-1.5 text-sm font-medium hover:opacity-80" aria-label="Zoom in">+</button>
                                                </div>
                                            </>
                                        ) : (
                                            <p className="text-gray-500 text-center p-8">{tracking_details?.c_title || popupAssets?.title || 'Welcome'}</p>
                                        )}
                                    </div>

                                    {/* Footer - description with microsite accent */}
                                    {showTrackingAsset && tracking_details?.c_description && (
                                        <div
                                            className="mt-3 px-3 py-2 rounded-xl border-l-4"
                                            style={{
                                                backgroundColor: siteColors.primary ? `${siteColors.primary}08` : '#f0f7ff',
                                                borderLeftColor: siteColors.primary || '#2167A7',
                                            }}
                                        >
                                            <p className="text-sm line-clamp-2" style={{ color: siteColors.textMuted || '#64748b' }}>
                                                {tracking_details.c_description}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </Transition>

            <div className='mt-[5rem] bg-[#fff]'>
                <section id='section1'>
                    <MainProfile data={ home_data?.microsite } colors={siteColors} url={window.location.origin + window.location.pathname} achivements={ home_data?.achivements?.data } ticker={home_data?.ticker} microsite_clt={home_data?.new_microsite_extra_clt} />
                </section>
                <section id='section2'>
                    <ProductCarousel data={ solution_data } colors={siteColors} scrollToSection={scrollToSection} microsite_clt={home_data?.new_microsite_extra_clt} />        
                </section>
                <section id='section3'>
                    <StoriesCarousel colors={siteColors} data={ home_data?.testimonials?.data } />
                </section>
                <section id='section4' style={{ backgroundColor: siteColors.primary }} className='bg-[#2167A7]'>
                    <LeadForm colors={siteColors} ticker={home_data?.ticker?.replace(/<[^>]+>/g, '')} />
                </section>
                    <section id='section7' style={{ backgroundColor: siteColors.primary }} className='bg-[#2167A7]'>
                        <Disclaimer colors={siteColors} microsite_clt={home_data?.new_microsite_extra_clt} />
                    </section>
                <WhatsappButton/>
                {home_data?.tracking_details?.c_title && (
                    <WhatsappPreviewModal tracking_details={home_data?.tracking_details} colors={siteColors} />
                )}
                
                </div>
            </IdleTimerProvider>
        </>
    );
}

export default MainPageContainer;
